import { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import BottomNavBar from "@/common/BottomNavBar";
import NotificationCard from "@/components/notifications/NotificationCard";
import NotificationSkeleton from "@/components/notifications/NotificationSkeleton";
import { requestForToken } from "@/config/firebase";
import Icons from "@/assets";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { isIOS } from "@/utils/helper";
import { useNotificationRefresh } from "@/hooks/useNotificationRefresh";
import { AppNotification } from "@/types/common";

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    maxWidth: "100%",
    overflowX: "hidden",
    bgcolor: "white",
    background: "linear-gradient(180deg, #7747FB 0%, #F2F2F2 15%)",
  },
  header: {
    py: "5%",
    px: "4%",
    color: "white",
  },
  contentContainer: {
    flex: 1,
    pb: "20%",
    overflowY: "auto",
  },
  centerContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
  },
  centerContentText: {
    fontSize: { xs: "4.85vw", sm: 20 },
    color: "#191919",
    fontWeight: 700,
  },
  centerContentText2: {
    fontSize: { xs: "3.4vw", sm: 14 },
    color: "#626262",
    mt: "0.5%",
  },
  centerContentIcon: {
    width: { xs: "24.27vw", sm: 100 },
    height: { xs: "24.27vw", sm: 100 },
    objectFit: "contain",
  },
  headerTitle: {
    fontSize: { xs: "4.85vw", sm: 20 },
    color: "#FFFFFF",
    fontWeight: 700,
  },
  errorText: {
    fontSize: { xs: "3.88vw", sm: 16 },
    color: "#191919",
  },
  reloadIcon: {
    width: { xs: "9.71vw", sm: 40 },
    height: { xs: "9.71vw", sm: 40 },
  },
  notificationsList: {
    mt: "5%",
  },
};

const Notifications = () => {
  const { api } = useApiClient();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [hasPermission] = useState<boolean>(
    isIOS() ||
      (typeof Notification !== "undefined" &&
        Notification.permission === "granted"),
  );
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const limit = 10;
  const { unreadCount, lastUpdated } = useSelector(
    (state: RootState) => state.notification,
  );

  const saveTokenToBackend = useCallback(async (token: string) => {
    try {
      await api.post(ENDPOINTS.USER_DEVICES.MAIN, { fcm_token: token });
      localStorage.setItem("fcm_token", token);
    } catch (error) {
      console.error("Error saving FCM token:", error);
    }
  }, [api]);

  const fetchNotifications = useCallback(async (pageNumber: number) => {
    try {
      if (pageNumber === 1) setLoading(true);
      else setIsFetchingMore(true);

      const res = await api.get(ENDPOINTS.NOTIFICATIONS.FETCH_ALL, {
        page: pageNumber,
        limit,
      });

      const { notifications: newNotifications, total } = res?.data?.data || {};

      if (pageNumber === 1) {
        setNotifications(newNotifications || []);
      } else {
        setNotifications((prev) => [...prev, ...(newNotifications || [])]);
      }

      setHasMore(total > pageNumber * limit);
      setPage(pageNumber);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [api, limit]);

  useEffect(() => {
    if (hasPermission) {
      requestForToken()?.then((token) => {
        if (token) {
          const savedToken = localStorage.getItem("fcm_token");
          if (savedToken !== token) {
            saveTokenToBackend(token);
          }
        }
      });
    } else {
      setLoading(false);
    }
  }, [hasPermission, saveTokenToBackend]);

  useEffect(() => {
    fetchNotifications(1);
  }, [unreadCount, lastUpdated, fetchNotifications]);

  // Auto-refetch notifications when shouldRefetch flag is set
  useNotificationRefresh(() => {
    if (hasPermission) {
      fetchNotifications(1);
    }
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      hasMore &&
      !loading &&
      !isFetchingMore
    ) {
      fetchNotifications(page + 1);
    }
  };

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={styles.header}>
        <Typography sx={styles.headerTitle}>Notifications</Typography>
      </Box>

      <Box sx={styles.contentContainer} onScroll={handleScroll}>
        {loading ? (
          Array.from(new Array(10)).map((_, index) => (
            <NotificationSkeleton key={index} />
          ))
        ) : !notifications?.length ? (
          <Box sx={styles.centerContent}>
            <Box
              component="img"
              src={Icons.Bell}
              sx={styles.centerContentIcon}
            />
            <Typography sx={styles.centerContentText}>
              No notifications yet
            </Typography>
            <Typography sx={styles.centerContentText2}>
              We'll notify you when something arrives
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.notificationsList}>
            {notifications?.map((n: AppNotification) => {
              return <NotificationCard key={n.notification_key} data={n} />;
            })}
            {isFetchingMore && (
              <Box>
                <NotificationSkeleton />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <BottomNavBar />
    </Box>
  );
};

export default Notifications;
